import { Gamepad, GenericPad, Scene, VirtualJoystick, Xbox360Button, Xbox360Pad } from '@babylonjs/core';
import { Level } from './Level';

export interface InputState {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  jumpPressed: boolean; // For detecting new jump press
  dash: boolean;
  // Analog movement values (-1 to 1) for smoother control
  movementX: number;
  movementY: number;
  // Camera rotation input from right stick (-1 to 1)
  cameraX: number;
  cameraY: number;
  throw: boolean;
  aim: boolean;
}

export class InputManager {
  private activeGamepad: Gamepad | null = null;
  private leftJoystick: VirtualJoystick | null = null;
  private rightJoystick: VirtualJoystick | null = null;

  // Keyboard state (from Map)
  private keys: Map<string, boolean> = new Map();

  // Gamepad input trackers
  private padLeft: boolean = false;
  private padRight: boolean = false;
  private padUp: boolean = false;
  private padDown: boolean = false;
  private padButtonA: boolean = false; // Jump
  private padButtonB: boolean = false; // Dash
  private padButtonX: boolean = false; // Throw
  private padButtonY: boolean = false; // Aim
  private padTriggerLeft: boolean = false;
  private padTriggerRight: boolean = false;

  // Mobile input trackers
  private mobileLeft: boolean = false;
  private mobileRight: boolean = false;
  private mobileUp: boolean = false;
  private mobileDown: boolean = false;
  private mobileButtonA: boolean = false; // Jump
  private mobileButtonB: boolean = false; // Dash
  private mobileButtonC: boolean = false; // Throw

  // State tracking
  private previousJump: boolean = false;
  private isMobile: boolean = false;

  public state: InputState = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    jumpPressed: false,
    dash: false,
    movementX: 0,
    movementY: 0,
    cameraX: 0,
    cameraY: 0,
    throw: false,
    aim: false,
  };

  public disabled: boolean = false;

  constructor(private level: Level) {
    const canvas = level.scene.getEngine().getRenderingCanvas();
    if (!canvas || level.game.config.sceneViewer) return;

    // Keyboard listeners
    canvas.addEventListener('keydown', (e) => this.onKeyDown(e));
    canvas.addEventListener('keyup', (e) => this.onKeyUp(e));
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));

    // Detect mobile/touch device
    this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Initialize gamepad support
    this.level.game.setupGamepad((gamepad) => {
      this.activeGamepad = gamepad;
      if (gamepad instanceof Xbox360Pad) {
        this.setupXboxPad(gamepad);
      } else if (gamepad instanceof GenericPad) {
        this.setupGenericPad(gamepad);
      }
    });

    // Initialize virtual joystick for mobile
    if (this.isMobile) {
      this.initVirtualJoystick(level.scene);
    }
  }

  private setupXboxPad(gamepad: Xbox360Pad): void {
    console.log('Xbox360Pad setup');

    this.level.game.removeableObservers.push(
      gamepad.onButtonDownObservable.add((button) => {
        switch (button) {
          case Xbox360Button.A:
            this.padButtonA = true;
            break;
          case Xbox360Button.B:
            this.padButtonB = true;
            break;
          case Xbox360Button.X:
            this.padButtonX = true;
            break;
          case Xbox360Button.Y:
            this.padButtonY = true;
            break;
          case Xbox360Button.LB:
            this.padTriggerLeft = true;
            break;
          case Xbox360Button.RB:
            this.padTriggerRight = true;
            break;
        }
      })
    );

    this.level.game.removeableObservers.push(
      gamepad.onButtonUpObservable.add((button) => {
        switch (button) {
          case Xbox360Button.A:
            this.padButtonA = false;
            break;
          case Xbox360Button.B:
            this.padButtonB = false;
            break;
          case Xbox360Button.X:
            this.padButtonX = false;
            break;
          case Xbox360Button.Y:
            this.padButtonY = false;
            break;
          case Xbox360Button.LB:
            this.padTriggerLeft = false;
            break;
          case Xbox360Button.RB:
            this.padTriggerRight = false;
            break;
        }
      })
    );
  }

  private setupGenericPad(gamepad: GenericPad): void {
    console.log(`GenericPad setup for: ${gamepad.id}`);

    this.level.game.removeableObservers.push(
      gamepad.onButtonDownObservable.add((button) => {
        switch (button) {
          case 0:
            this.padButtonA = true; // Usually A/X - Jump
            break;
          case 1:
            this.padButtonB = true; // Usually B/Circle - Dash
            break;
          case 2:
            this.padButtonX = true; // Usually X/Square
            break;
          case 3:
            this.padButtonY = true; // Usually Y/Triangle
            break;
          case 4:
            this.padTriggerLeft = true;
            break;
          case 5:
            this.padTriggerRight = true;
            break;
          // DPad buttons (some controllers)
          case 12:
            this.padUp = true;
            break;
          case 13:
            this.padDown = true;
            break;
          case 14:
            this.padLeft = true;
            break;
          case 15:
            this.padRight = true;
            break;
        }
      })
    );

    this.level.game.removeableObservers.push(
      gamepad.onButtonUpObservable.add((button) => {
        switch (button) {
          case 0:
            this.padButtonA = false;
            break;
          case 1:
            this.padButtonB = false;
            break;
          case 2:
            this.padButtonX = false;
            break;
          case 3:
            this.padButtonY = false;
            break;
          case 4:
            this.padTriggerLeft = false;
            break;
          case 5:
            this.padTriggerRight = false;
            break;
          case 12:
            this.padUp = false;
            break;
          case 13:
            this.padDown = false;
            break;
          case 14:
            this.padLeft = false;
            break;
          case 15:
            this.padRight = false;
            break;
        }
      })
    );
  }

  private resetPadState(): void {
    this.padLeft = false;
    this.padRight = false;
    this.padUp = false;
    this.padDown = false;
    this.padButtonA = false;
    this.padButtonB = false;
    this.padButtonX = false;
    this.padButtonY = false;
    this.padTriggerLeft = false;
    this.padTriggerRight = false;
  }

  private initVirtualJoystick(scene: Scene): void {
    // Create left joystick for movement (left side = true)
    this.leftJoystick = new VirtualJoystick(true);

    // Create right joystick for camera/aiming (left side = false)
    this.rightJoystick = new VirtualJoystick(false);

    // VirtualJoystick canvas z-index is typically 5
    // We'll create HTML buttons with higher z-index
    const joystickCanvas = VirtualJoystick.Canvas;
    if (joystickCanvas) {
      joystickCanvas.style.zIndex = '5';
    }

    // Create HTML-based buttons (above joystick canvas)
    this.createMobileButtons();
  }

  private mobileButtonContainer: HTMLDivElement | null = null;

  private createMobileButtons(): void {
    // Create container for mobile buttons
    this.mobileButtonContainer = document.createElement('div');
    this.mobileButtonContainer.id = 'mobileButtonContainer';
    this.mobileButtonContainer.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 10;
      display: flex;
      gap: 20px;
      pointer-events: none;
    `;
    document.body.appendChild(this.mobileButtonContainer);

    const bgColor = 'rgba(0,0,0,0.5)';
    const size = 80;

    // Jump button
    const jumpBtn = this.createMobileButton('mobileButtonA', '🥾', bgColor, size);
    this.mobileButtonContainer.appendChild(jumpBtn);

    // Throw button
    const throwBtn = this.createMobileButton('mobileButtonC', '🍎', bgColor, size);
    this.mobileButtonContainer.appendChild(throwBtn);

    // Dash button
    const dashBtn = this.createMobileButton('mobileButtonB', '🏂', bgColor, size);
    this.mobileButtonContainer.appendChild(dashBtn);
  }

  private createMobileButton(property: string, icon: string, bgColor: string, size: number): HTMLButtonElement {
    const btn = this.createButton(icon, bgColor, size);
    btn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this[property] = true;
    });
    btn.addEventListener('touchend', () => {
      this[property] = false;
    });
    btn.addEventListener('touchcancel', () => {
      this[property] = false;
    });
    return btn;
  }

  private createButton(emoji: string, bgColor: string, size: number): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.innerHTML = emoji;
    btn.style.cssText = `
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 3px solid white;
      background: ${bgColor};
      font-size: ${size * 0.4}px;
      cursor: pointer;
      pointer-events: auto;
      touch-action: none;
      user-select: none;
      -webkit-user-select: none;
    `;
    return btn;
  }

  private onKeyDown(event: KeyboardEvent) {
    this.keys.set(event.code, true);
  }

  private onKeyUp(event: KeyboardEvent) {
    this.keys.set(event.code, false);
  }

  update() {
    if (this.disabled) {
      // Reset state when disabled
      this.state.forward = false;
      this.state.backward = false;
      this.state.left = false;
      this.state.right = false;
      this.state.jump = false;
      this.state.jumpPressed = false;
      this.state.dash = false;
      this.state.movementX = 0;
      this.state.movementY = 0;
      this.state.throw = false;
      return;
    }

    // === Keyboard Input ===
    // WASD for movement only
    const keyForward = this.keys.get('KeyW') || false;
    const keyBackward = this.keys.get('KeyS') || false;
    const keyLeft = this.keys.get('KeyA') || false;
    const keyRight = this.keys.get('KeyD') || false;
    const keyJump = this.keys.get('Space') || false;
    const keyDash = this.keys.get('KeyQ') || false;
    const keyThrow = this.keys.get('KeyE') || false;
    const keyAim = this.keys.get('ShiftLeft') || false;

    // Arrow keys for camera
    const keyCameraLeft = this.keys.get('ArrowLeft') || false;
    const keyCameraRight = this.keys.get('ArrowRight') || false;

    // === Gamepad Stick Input ===
    let padStickX = 0;
    let padStickY = 0;
    let padRightStickX = 0;
    let padRightStickY = 0;
    const deadzone = 0.15;

    if (this.activeGamepad) {
      const leftStick = this.activeGamepad.leftStick;
      if (leftStick) {
        if (Math.abs(leftStick.x) > deadzone) {
          padStickX = leftStick.x;
        }
        if (Math.abs(leftStick.y) > deadzone) {
          // Y is inverted on gamepads (up = negative)
          padStickY = -leftStick.y;
        }
      }

      // Right stick for camera control
      const rightStick = this.activeGamepad.rightStick;
      if (rightStick) {
        if (Math.abs(rightStick.x) > deadzone) {
          padRightStickX = rightStick.x;
        }
        if (Math.abs(rightStick.y) > deadzone) {
          // Y is inverted on gamepads (up = negative)
          padRightStickY = -rightStick.y;
        }
      }
    }

    // === Mobile Joystick Input ===
    let mobileStickX = 0;
    let mobileStickY = 0;

    if (this.leftJoystick && this.leftJoystick.pressed) {
      const joyPos = this.leftJoystick.deltaPosition;
      // deltaPosition values are already normalized (0-1 range)
      // Just clamp and use directly
      mobileStickX = Math.max(-1, Math.min(1, joyPos.x));
      mobileStickY = Math.max(-1, Math.min(1, joyPos.y)); // Y axis (positive = forward)
    }

    // === Combine Movement ===
    // Priority: Keyboard (digital) > Gamepad DPad (digital) > Gamepad Stick (analog) > Mobile Joystick (analog)
    let movementX = 0;
    let movementY = 0;

    // Keyboard digital input
    if (keyForward) movementY = 1;
    if (keyBackward) movementY = -1;
    if (keyLeft) movementX = -1;
    if (keyRight) movementX = 1;

    // Pad DPad digital input (OR with keyboard)
    if (this.padUp) movementY = 1;
    if (this.padDown) movementY = -1;
    if (this.padLeft) movementX = -1;
    if (this.padRight) movementX = 1;

    // If no digital input, use analog stick
    if (movementX === 0 && movementY === 0) {
      if (Math.abs(padStickX) > 0 || Math.abs(padStickY) > 0) {
        movementX = padStickX;
        movementY = padStickY;
      } else if (Math.abs(mobileStickX) > 0.05 || Math.abs(mobileStickY) > 0.05) {
        movementX = mobileStickX;
        movementY = mobileStickY;
      }
    }

    // Mobile DPad (if virtual joystick gives digital-like values)
    if (this.mobileUp) movementY = 1;
    if (this.mobileDown) movementY = -1;
    if (this.mobileLeft) movementX = -1;
    if (this.mobileRight) movementX = 1;

    // === Update State ===
    this.state.movementX = movementX;
    this.state.movementY = movementY;

    // Boolean movement from analog (for backward compatibility)
    this.state.forward = movementY > 0.1;
    this.state.backward = movementY < -0.1;
    this.state.left = movementX < -0.1;
    this.state.right = movementX > 0.1;

    // === Combine Buttons ===
    // Jump: OR all sources
    const currentJump = keyJump || this.padButtonA || this.mobileButtonA;
    this.state.jumpPressed = currentJump && !this.previousJump;
    this.state.jump = currentJump;
    this.previousJump = currentJump;

    // Dash: OR all sources
    this.state.dash = keyDash || this.padButtonB || this.mobileButtonB;

    // === Camera Input (Right Stick + Arrow Keys + Mobile Right Joystick) ===
    let cameraX = padRightStickX;
    let cameraY = padRightStickY;

    if (this.rightJoystick && this.rightJoystick.pressed) {
      const joyPos = this.rightJoystick.deltaPosition;
      cameraX = Math.max(-1, Math.min(1, joyPos.x));
      cameraY = Math.max(-1, Math.min(1, joyPos.y));
    }

    // Arrow keys override stick for horizontal camera (lower value for slower rotation)
    if (keyCameraLeft) cameraX = -0.4;
    if (keyCameraRight) cameraX = 0.4;

    // Arrow keys for vertical camera
    const keyCameraUp = this.keys.get('ArrowUp') || false;
    const keyCameraDown = this.keys.get('ArrowDown') || false;

    if (keyCameraUp) cameraY = 0.4;
    if (keyCameraDown) cameraY = -0.4;

    this.state.cameraX = cameraX;
    this.state.cameraY = cameraY;

    // Throwing
    this.state.throw = keyThrow || this.padButtonX || this.mobileButtonC;

    // Aiming
    this.state.aim = keyAim || this.padTriggerLeft;
  }

  hasMovementInput(): boolean {
    return (
      this.state.forward ||
      this.state.backward ||
      this.state.left ||
      this.state.right ||
      Math.abs(this.state.movementX) > 0.1 ||
      Math.abs(this.state.movementY) > 0.1
    );
  }

  dispose() {
    this.keys.clear();

    if (this.leftJoystick) {
      this.leftJoystick.releaseCanvas();
      this.leftJoystick = null;
    }

    if (this.rightJoystick) {
      this.rightJoystick.releaseCanvas();
      this.rightJoystick = null;
    }

    if (this.mobileButtonContainer) {
      this.mobileButtonContainer.remove();
      this.mobileButtonContainer = null;
    }

    this.resetPadState();
  }
}
